import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../i18n';
import { FileError } from '../../components/FileError';
import { FILE_LIMITS, validateFile } from '../../lib/fileValidation';
import {
  PLATFORMS,
  PLATFORM_LINKEDIN,
  PLATFORM_INSTAGRAM,
  PLATFORM_FACEBOOK,
  TONES,
} from '../../lib/constants';

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let value = bytes;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function makeId() {
  return Math.random().toString(36).slice(2, 11);
}

const DEFAULT_PLATFORMS = [PLATFORM_LINKEDIN, PLATFORM_INSTAGRAM, PLATFORM_FACEBOOK];

function formatTimer(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mock voice recorder — pulsing red dot, timer, stop button
// ─────────────────────────────────────────────────────────────────────────────
function VoiceRecorder({ onTranscript }) {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState('');
  const intervalRef = useRef(null);

  function start() {
    setTranscript('');
    setSeconds(0);
    setIsRecording(true);
    intervalRef.current = setInterval(() => {
      setSeconds((prev) => prev + 1);
    }, 1000);
  }

  function stop() {
    setIsRecording(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    // TODO(speech): replace with Web Speech API transcription on stop.
    setTranscript(t('quickPost.voiceTranscriptPlaceholder'));
    onTranscript?.(t('quickPost.voiceTranscriptPlaceholder'));
  }

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div className={'voice-recorder' + (isRecording ? ' voice-recorder--recording' : '')}>
      {isRecording ? (
        <>
          <span className="voice-recorder__dot" aria-hidden="true" />
          <span className="voice-recorder__status">{t('quickPost.voiceRecording')}</span>
          <span className="voice-recorder__timer">{formatTimer(seconds)}</span>
          <button type="button" className="voice-recorder__stop" onClick={stop}>
            {t('quickPost.voiceStop')}
          </button>
        </>
      ) : (
        <>
          <button type="button" className="voice-recorder__button" onClick={start}>
            <span aria-hidden="true">🎤</span> {t('quickPost.voiceRecord')}
          </button>
          <span className="voice-recorder__hint">{t('quickPost.voiceHint')}</span>
        </>
      )}
      {transcript && !isRecording && (
        <div className="voice-recorder__transcript">{transcript}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline drop zone — same visual pattern as UploadPage but smaller and
// integrated into the Quick Post form. Image-or-video, validated client-side.
// ─────────────────────────────────────────────────────────────────────────────
function QuickPostDropZone({ files, onAdd, onRemove }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [active, setActive] = useState(false);

  function handleDrop(event) {
    event.preventDefault();
    setActive(false);
    if (event.dataTransfer.files?.length) onAdd(event.dataTransfer.files);
  }
  function handleSelect(event) {
    if (event.target.files?.length) onAdd(event.target.files);
    event.target.value = '';
  }

  return (
    <div className="form-field">
      <span className="form-field__label">{t('quickPost.media')}</span>
      <div
        className={'dropzone dropzone--compact' + (active ? ' dropzone--active' : '')}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setActive(true); }}
        onDragLeave={() => setActive(false)}
        onDrop={handleDrop}
      >
        <div className="dropzone__icon" aria-hidden="true">📤</div>
        <div className="dropzone__title">{t('quickPost.dropZone')}</div>
        <div className="dropzone__limits">
          {t('upload.acceptedImages', { limit: FILE_LIMITS.imageMb })}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/heic,image/gif,video/mp4,video/quicktime,video/webm"
          onChange={handleSelect}
          style={{ display: 'none' }}
        />
      </div>

      {files.length > 0 && (
        <div className="file-strip" aria-label={t('upload.selectedFiles')}>
          {files.map((file) => (
            <div
              key={file.id}
              className={
                'file-card' +
                (file.valid ? ' file-card--accepted' : ' file-card--rejected')
              }
            >
              <button
                type="button"
                className="file-card__remove"
                onClick={() => onRemove(file.id)}
                aria-label={t('upload.removeFile')}
              >
                ×
              </button>
              <div className="file-card__thumb">
                {file.previewUrl ? (
                  <img src={file.previewUrl} alt="" />
                ) : (
                  <span aria-hidden="true">{file.kind === 'video' ? '🎬' : '📄'}</span>
                )}
              </div>
              <div className="file-card__name" title={file.name}>{file.name}</div>
              <div className="file-card__meta">{formatBytes(file.size)}</div>
              {!file.valid && (
                <FileError errorKey={file.errorKey} params={file.errorParams} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export function QuickPost() {
  const { t } = useTranslation();
  const [context, setContext] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState(new Set(DEFAULT_PLATFORMS));
  const [tone, setTone] = useState('');
  const [files, setFiles] = useState([]);

  function togglePlatform(platform) {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });
  }

  function addFiles(fileList) {
    const newFiles = Array.from(fileList).map((file) => {
      const result = validateFile(file, 'media');
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      return {
        id: makeId(),
        name: file.name,
        size: file.size,
        kind: isVideo ? 'video' : 'image',
        previewUrl: isImage && result.valid ? URL.createObjectURL(file) : null,
        valid: result.valid,
        errorKey: result.valid ? null : result.errorKey,
        errorParams: result.valid ? null : result.params,
      };
    });
    setFiles((prev) => [...prev, ...newFiles]);
  }

  function removeFile(id) {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  }

  useEffect(() => {
    return () => {
      files.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validFiles = files.filter((f) => f.valid);
  const canGenerate = context.trim().length > 0 || validFiles.length > 0;

  function handleGenerate() {
    if (!canGenerate) return;
    // TODO(api): replace with POST /api/v1/generate once the endpoint lands.
    // For now, the button is a stub — log the inputs that would be sent.
    console.log('[quick-post] generate', {
      context: context.trim(),
      tone,
      platforms: [...selectedPlatforms],
      fileNames: validFiles.map((f) => f.name),
    });
  }

  return (
    <div className="quick-post">
      <header className="page-header">
        <h1 className="page-header__title">{t('quickPost.title')}</h1>
        <div className="page-header__subtitle">{t('quickPost.subtitle')}</div>
      </header>

      <form className="qp-form" onSubmit={(e) => e.preventDefault()}>
        <label className="form-field">
          <span className="form-field__label">{t('quickPost.contextLabel')}</span>
          <textarea
            className="form-field__textarea"
            value={context}
            placeholder={t('quickPost.contextPlaceholder')}
            onChange={(e) => setContext(e.target.value)}
            rows={5}
          />
        </label>

        <QuickPostDropZone files={files} onAdd={addFiles} onRemove={removeFile} />

        <div className="form-field">
          <span className="form-field__label">{t('quickPost.platforms')}</span>
          <div className="checkbox-group">
            {PLATFORMS.filter((p) => p !== 'youtube').map((platform) => (
              <label key={platform} className="checkbox-group__item">
                <input
                  type="checkbox"
                  checked={selectedPlatforms.has(platform)}
                  onChange={() => togglePlatform(platform)}
                />
                {t(`platforms.${platform}`)}
              </label>
            ))}
          </div>
        </div>

        <label className="form-field">
          <span className="form-field__label">{t('quickPost.tone')}</span>
          <select
            className="form-field__select"
            value={tone}
            onChange={(e) => setTone(e.target.value)}
          >
            <option value="">{t('quickPost.tonePlaceholder')}</option>
            {TONES.map((value) => (
              <option key={value} value={value}>{t(`draft.tones.${value}`)}</option>
            ))}
          </select>
        </label>

        <VoiceRecorder onTranscript={(text) => setContext(text)} />

        <div className="upload-form__actions">
          <button
            type="button"
            className="button button--primary"
            disabled={!canGenerate}
            onClick={handleGenerate}
          >
            {t('quickPost.generate')}
          </button>
        </div>
      </form>

      <div className="empty-drafts">{t('quickPost.emptyDrafts')}</div>
    </div>
  );
}
